import type { PendingConnectorWrite } from "@/lib/connectorPendingWrite";
import type { ContactRecord, ContactProvider, ContactWriteAction, ContactRequest } from "../../supabase/functions/_shared/contactDomain";
export type { ContactRecord, ContactProvider, ContactWriteAction, ContactRequest } from "../../supabase/functions/_shared/contactDomain";

export type ContactAction = "search" | "create" | "update" | "delete";
export interface ContactActionPrefill {
  action: ContactAction;
  provider: ContactProvider | "auto";
  query?: string;
  item_id?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  phone?: string;
  action_id: string;
  expected_user_id?: string;
  expected_session_id?: string;
}
export interface ContactResponse extends Record<string, unknown> {
  ok: true;
  provider: ContactProvider;
  items?: ContactRecord[];
  item?: ContactRecord;
  complete?: boolean;
  has_more?: boolean;
  coverage?: string;
  address_book?: string;
}

export class ContactExecutorError extends Error {
  readonly safe_retry: boolean;
  constructor(code: string, public context?: Response, safeRetry = false) {
    super(code); this.name = "ContactExecutorError"; this.safe_retry = safeRetry;
  }
}
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

/** A locator only. Remote proof is verified from the owner's server receipt. */
export interface MirrorContactReceiptReference {
  provider: ContactProvider;
  capability: ContactWriteAction;
  idempotency_key: string;
  action_id: string;
  userId: string;
  sessionId: string;
}
export interface MirrorContactOrigin { userId: string; sessionId: string; actionId: string }
export function matchesMirrorContactReceiptOrigin(value: unknown, origin: MirrorContactOrigin): value is MirrorContactReceiptReference {
  return object(value) && !!origin.userId && !!origin.sessionId && !!origin.actionId && value.userId === origin.userId &&
    value.sessionId === origin.sessionId && value.action_id === origin.actionId &&
    ["google_contacts", "microsoft_contacts"].includes(String(value.provider)) &&
    ["create_contact", "update_contact", "delete_contact"].includes(String(value.capability)) &&
    value.idempotency_key === "mirror_contact_" + origin.actionId + "_" + value.capability + "_0";
}
export function mirrorContactReceiptReference(entry: PendingConnectorWrite, origin: MirrorContactOrigin): MirrorContactReceiptReference | null {
  const body = entry.body;
  const reference = { provider: body.provider, capability: body.action, idempotency_key: body.idempotency_key,
    action_id: origin.actionId, userId: origin.userId, sessionId: origin.sessionId };
  if (entry.group !== "contact" || entry.key !== origin.userId + ":contact" || entry.ownerId !== origin.userId ||
    entry.status !== "succeeded" || !object(body.mirror_context) || body.mirror_context.session_id !== origin.sessionId ||
    body.mirror_context.action_id !== origin.actionId || !matchesMirrorContactReceiptOrigin(reference, origin)) return null;
  return reference;
}

export function parseContactRecord(value: unknown): ContactRecord | null {
  if (!object(value) || typeof value.id !== "string" || !value.id || value.id.length > 1500 ||
    !["title", "given_name", "family_name", "version"].every(key => typeof value[key] === "string") ||
    !["writable", "delete_writable", "read_only", "content_truncated"].every(key => typeof value[key] === "boolean") ||
    !Array.isArray(value.emails) || value.emails.length > 30 || !Array.isArray(value.phones) || value.phones.length > 30 ||
    value.emails.some(entry => !object(entry) || typeof entry.address !== "string" || !entry.address ||
      entry.primary !== undefined && typeof entry.primary !== "boolean") ||
    value.phones.some(entry => !object(entry) || typeof entry.number !== "string" || !entry.number ||
      entry.primary !== undefined && typeof entry.primary !== "boolean")) return null;
  return value as unknown as ContactRecord;
}

export async function contactOwnerSession(ownerId: string) {
  const { supabase } = await import("@/integrations/supabase/client");
  const user = await supabase.auth.getUser();
  if (user.error || user.data.user?.id !== ownerId) throw new ContactExecutorError("invalid_session");
  const session = await supabase.auth.getSession();
  if (session.error || session.data.session?.user.id !== ownerId || !session.data.session.access_token) throw new ContactExecutorError("invalid_session");
  return session.data.session;
}

/** Explicit write-consent only; the contact draft is never dispatched here. */
export async function enableContactChanges(provider: ContactProvider, ownerId: string, isCurrent: () => boolean): Promise<void> {
  const [{ supabase }, { platformOpen }] = await Promise.all([
    import("@/integrations/supabase/client"), import("@/lib/platformOpen"),
  ]);
  if (!isCurrent()) throw new ContactExecutorError("invalid_session");
  const session = await contactOwnerSession(ownerId);
  if (!isCurrent()) throw new ContactExecutorError("invalid_session");
  const response = await supabase.functions.invoke("people-tasks-oauth-init", {
    body: { provider, write_access: true }, headers: { Authorization: "Bearer " + session.access_token },
  });
  await contactOwnerSession(ownerId);
  if (!isCurrent()) throw new ContactExecutorError("invalid_session");
  if (response.error || typeof response.data?.authorize_url !== "string") throw new ContactExecutorError("connection_failed");
  let url: URL;
  try { url = new URL(response.data.authorize_url); } catch { throw new ContactExecutorError("connection_failed"); }
  if (url.protocol !== "https:") throw new ContactExecutorError("connection_failed");
  platformOpen(url.href);
}

/** Every request uses this owner's token, and every response rechecks ownership. */
export async function invokeContact(body: ContactRequest, ownerId: string): Promise<ContactResponse> {
  if (!ownerId) throw new ContactExecutorError("invalid_session");
  const session = await contactOwnerSession(ownerId);
  const { supabase } = await import("@/integrations/supabase/client");
  const response = await supabase.functions.invoke("contact-executor", {
    body, headers: { Authorization: "Bearer " + session.access_token },
  });
  await contactOwnerSession(ownerId);
  if (response.error) {
    const context = (response.error as { context?: Response }).context;
    if (context && typeof context.clone === "function") {
      try {
        const payload = await context.clone().json();
        if (typeof payload?.error === "string") throw new ContactExecutorError(payload.error, context, payload.safe_retry === true);
      } catch (cause) { if (cause instanceof ContactExecutorError) throw cause; }
    }
    throw new ContactExecutorError("outcome_unknown", context);
  }
  if (typeof response.data?.error === "string") throw new ContactExecutorError(response.data.error, undefined, response.data.safe_retry === true);
  if (!object(response.data) || response.data.ok !== true || response.data.provider !== body.provider) throw new ContactExecutorError("outcome_unknown");
  return response.data as ContactResponse;
}

export const contactWriteAction = (action: Exclude<ContactAction, "search">): ContactWriteAction => action + "_contact" as ContactWriteAction;

export function primaryContactEmail(item: ContactRecord): string | null {
  const primary = item.emails.filter(entry => entry.primary === true);
  return primary.length === 1 ? primary[0].address : item.emails.length === 1 ? item.emails[0].address : item.emails.length === 0 ? "" : null;
}
export function primaryContactPhone(item: ContactRecord): string | null {
  const primary = item.phones.filter(entry => entry.primary === true);
  return primary.length === 1 ? primary[0].number : item.phones.length === 1 ? item.phones[0].number : item.phones.length === 0 ? "" : null;
}

/** Microsoft edits the mobile field, never infers a primary home/work phone. */
export function editableContactPhone(provider: ContactProvider, item: ContactRecord): string | null {
  if (provider !== "microsoft_contacts") return primaryContactPhone(item);
  const mobile = item.phones.filter(entry => entry.type === "mobile");
  return mobile.length === 1 ? mobile[0].number : mobile.length === 0 ? "" : null;
}
